'use client';

// Third Party Plugins
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import queryString from 'query-string';
import netlifyDataEncoder from '@utils/netlifyDataEncoder';
// Types
import { FormComponentProps } from './types';
// Styles
import {
  StyledForm,
  StyledFormContent,
  StyledFormRow,
  StyledSelectContainer,
  StyledLabel,
  StyledSelect,
} from './styles';
// Custom Components
import FormInput from '@/components/forms/FormInput';
import Button from '@/components/buttons/BaseButton';

const FormComponent: React.FC<FormComponentProps> = ({ instructions }) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      inquiryType: 'generalInquiry',
    },
  });

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (params.source) {
      setValue('inquiryType', params.source as string);
    }
  }, [setValue]);

  const formSubmit: SubmitHandler<FieldValues> = (formData, e) => {
    const form = e?.target as HTMLFormElement;
    fetch('/__forms.html', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: netlifyDataEncoder({
        'form-name': form.getAttribute('name') || 'contact',
        ...formData,
      }),
    })
      .then(() => {
        const action = form.getAttribute('action');
        if (action) {
          window.location.href = action;
        }
      })
      .catch((error) => alert('Form submission failed: ' + error.message));
  };

  return (
    <StyledForm
      name="contact"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      action="/thank-you"
      onSubmit={handleSubmit(formSubmit)}
    >
      <input type="hidden" name="form-name" value="contact" />
      {instructions && <p>{instructions}</p>}
      <StyledFormContent>
        <StyledFormRow>
          <FormInput
            label="First Name*"
            id="firstName"
            register={register}
            type="text"
            errors={errors}
          />
          <FormInput
            label="Last Name*"
            id="lastName"
            register={register}
            type="text"
            errors={errors}
          />
        </StyledFormRow>
        <FormInput
          label="Email*"
          id="email"
          register={register}
          type="email"
          placeholder="info@example.com"
          errors={errors}
        />
        <Controller
          name="inquiryType"
          control={control}
          rules={{ required: `Please select an inquiry type.` }}
          render={({ field }) => (
            <StyledSelectContainer>
              <StyledLabel htmlFor="inquiryType">Inquiry Type*</StyledLabel>
              <StyledSelect id="inquiryType" {...field}>
                <option value="generalInquiry">General Inquiry</option>
                <option value="add-book">Add Book</option>
                <option value="add-resource">Add Resource</option>
                <option value="scholarship">Scholarship</option>
                <option value="grant">Grant</option>
                <option value="student-animation-showcase">
                  Student Animation Showcase
                </option>
              </StyledSelect>
            </StyledSelectContainer>
          )}
        />
        <FormInput
          label="Message*"
          id="message"
          register={register}
          placeholder="Write something…"
          errors={errors}
          isTextarea
          rows={16}
        />
        <Button type="submit">Submit</Button>
      </StyledFormContent>
    </StyledForm>
  );
};

export default FormComponent;
